import { useCallback } from 'react'

import {
  connectWallet,
  getChainId,
  signTypedDataPayload,
} from '@/lib/hyperliquid/sign-approve-agent'
import type { HlSelectionState } from '@/lib/hyperliquid/subaccount-selection'
import {
  hyperliquidConfirmAgent,
  hyperliquidLinkSubaccounts,
  hyperliquidListSubaccounts,
  hyperliquidPrepareAgent,
} from '@/service/venue.api'

export function useHyperliquidOnboard() {
  // Returns the connected master address (not just void) so the wizard can
  // drive the post-onboard listSubaccounts call without reconnecting the
  // wallet a second time.
  const onboard = useCallback(async (accountId: string): Promise<string> => {
    // Connect first so we can read the wallet's active chain. The backend stamps
    // this into the EIP-712 domain so MetaMask (which enforces
    // domain.chainId === active chain) will sign without a network switch.
    const masterAddress = await connectWallet()
    const signatureChainId = await getChainId()

    // Prepare immediately before prompting — the agent payload is single-use, 10-min TTL.
    // masterAddress lets the backend pre-check "already linked to another OrderX
    // account" before the user signs, instead of only discovering it at confirm time.
    const prepared = await hyperliquidPrepareAgent({ accountId, signatureChainId, masterAddress })

    const approveAgentSignature = await signTypedDataPayload(
      masterAddress,
      prepared.approveAgentPayload
    )

    // Builder-fee is first-class: sign it too whenever the backend returns a payload.
    let approveBuilderFeeSignature: string | undefined
    if (prepared.approveBuilderFeePayload) {
      approveBuilderFeeSignature = await signTypedDataPayload(
        masterAddress,
        prepared.approveBuilderFeePayload
      )
    }

    await hyperliquidConfirmAgent({
      accountId,
      masterAddress,
      approveAgentSignature,
      approveBuilderFeeSignature,
    })

    return masterAddress
  }, [])

  const listSubaccounts = useCallback(async (accountId: string, masterAddress: string) => {
    return hyperliquidListSubaccounts({ accountId, masterAddress })
  }, [])

  const linkSubaccounts = useCallback(
    async (accountId: string, masterAddress: string, selection: HlSelectionState) => {
      return hyperliquidLinkSubaccounts({
        accountId,
        masterAddress,
        subaccountAddresses: selection.selected,
      })
    },
    []
  )

  return { onboard, listSubaccounts, linkSubaccounts }
}
